import { useState } from 'react'
import { ChevronDown, ChevronUp, Repeat } from 'lucide-react'
import { Button, MoneyValue, Skeleton, StatusBadge } from '@shared/design-system/ui'
import { useMediaQuery } from '@shared/lib/use-media-query'
import {
  getChargeStatusPresentation,
  getJourneyStatusPresentation,
  SaleImpactCopy,
  subscriptionChargeExtraTags,
  subscriptionChargeLabel,
  summarizeSubscriptionCharge,
  individualSubscriptionCharges,
  summarizeJourney,
  summarizeJourneyStatuses,
  useSaleChargesQuery,
} from '@entities/sale'
import type { SalesRow, SaleJourney, SubscriptionCharge, SubscriptionGroup } from '@entities/sale'
import {
  alignRight,
  buyerCell,
  buyerName,
  buyerProduct,
  cycleAside,
  cycleAmount,
  cycleLead,
  cycleList,
  cycleMeta,
  cycleRow,
  cycleTitle,
  cycleTitleRow,
  expandCell,
  expandToggle,
  funnelTag,
  hideOnMobile,
  mobileCard,
  mobileCardAside,
  mobileCardButton,
  mobileCardDetail,
  mobileCardDetails,
  mobileCardHeader,
  mobileCardInteractive,
  mobileCardLead,
  mobileCardMeta,
  mobileCardTitle,
  mobileDetailLabel,
  mobileDetailValue,
  mobileList,
  pendingHint,
  productRow,
  row,
  subBadgeRow,
  subFooter,
  table,
  td,
  th,
} from './sales.css'
import { SaleIdHint } from './SaleIdHint'

type SalesTableProps = {
  rows: SalesRow[]
  onSelect: (saleId: string, scope?: 'charge') => void
  onViewSubscription: (subscriptionId: string) => void
}

function hasCouponIndicator(sale: { coupon?: unknown }): boolean {
  return sale.coupon !== undefined
}

function journeyHasCoupon(journey: SaleJourney): boolean {
  return [journey.sale, ...journey.children].some((charge) => hasCouponIndicator(charge))
}

function subscriptionChargeHasCoupon(charge: SubscriptionCharge): boolean {
  return [charge.sale, ...charge.funnelChildren].some(hasCouponIndicator)
}

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function formatMonth(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(new Date(iso))
}

function embeddedItemLabel(count: number): string {
  return count === 1 ? '+1 item na cobrança' : `+${count} itens na cobrança`
}

function separateChargeLabel(count: number): string {
  return count === 1 ? '+1 cobrança do funil' : `+${count} cobranças do funil`
}

function chargeCountLabel(count: number): string {
  return count === 1 ? '1 cobrança' : `${count} cobranças`
}

function cycleLabel(charge: SubscriptionCharge, group: SubscriptionGroup): string {
  return subscriptionChargeLabel(charge, group)
}

function healthBadge(group: SubscriptionGroup): {
  label: string
  tone: 'success' | 'awaiting' | 'closed'
} {
  if (group.health === 'overdue') {
    return { label: 'Em atraso', tone: 'awaiting' }
  }
  if (group.health === 'ended') {
    return { label: 'Encerrada', tone: 'closed' }
  }
  return { label: 'Assinatura ativa', tone: 'success' }
}

function JourneyRow({
  journey,
  onSelect,
}: {
  journey: SaleJourney
  onSelect: (saleId: string, scope?: 'charge') => void
}) {
  const { sale } = journey
  const summary = summarizeJourney(journey)
  const statuses = summarizeJourneyStatuses(journey)
  const presentation = getJourneyStatusPresentation({ ...sale, funnelStages: journey.children })
  const StatusIcon = presentation.icon
  const hasCoupon = journeyHasCoupon(journey)
  return (
    <tr
      className={row}
      role="button"
      tabIndex={0}
      aria-label={`Ver detalhe da venda de ${sale.buyerName}: ${sale.productName}`}
      onClick={() => {
        onSelect(sale.id)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect(sale.id)
        }
      }}
    >
      <td className={td}>
        <div className={buyerCell}>
          <span className={buyerName}>{sale.buyerName}</span>
          <span className={productRow}>
            <span className={buyerProduct}>{sale.productName}</span>
            {hasCoupon ? <span className={funnelTag}>Cupom</span> : null}
            {sale.affiliateSale === true ? <span className={funnelTag}>Afiliado</span> : null}
            {summary.embeddedItemCount > 0 ? (
              <span className={funnelTag}>{embeddedItemLabel(summary.embeddedItemCount)}</span>
            ) : null}
            {summary.separateChargeCount > 0 ? (
              <span className={funnelTag}>{separateChargeLabel(summary.separateChargeCount)}</span>
            ) : null}
            {sale.globalMarket ? <span className={funnelTag}>Mercado Global</span> : null}
          </span>
          <SaleIdHint saleId={sale.id} />
        </div>
      </td>
      <td className={`${td} ${hideOnMobile}`}>{formatDateTime(sale.createdAt)}</td>
      <td className={td}>
        <StatusBadge
          tone={presentation.tone}
          label={presentation.label}
          size="sm"
          icon={<StatusIcon size={13} />}
        />
        {statuses.chargeCount > 1 ? <span className={pendingHint}>{statuses.label}</span> : null}
      </td>
      <td className={`${td} ${alignRight}`}>
        {summary.hasFinancialImpact ? (
          <SaleImpactCopy
            active={summary.total}
            original={summary.originalTotal}
            affected={summary.affectedTotal}
            align="end"
          />
        ) : (
          <MoneyValue amount={summary.total.amount} currency={summary.total.currency} size="sm" />
        )}
      </td>
    </tr>
  )
}

function SubscriptionRow({
  group,
  expanded,
  onToggle,
  onSelect,
  onViewSubscription,
}: {
  group: SubscriptionGroup
  expanded: boolean
  onToggle: () => void
  onSelect: (saleId: string, scope?: 'charge') => void
  onViewSubscription: (subscriptionId: string) => void
}) {
  const badge = healthBadge(group)
  const overdueHint =
    group.health === 'overdue'
      ? group.latestStatus === 'FAILED'
        ? 'última cobrança falhou'
        : 'última cobrança em aberto'
      : null

  return (
    <>
      <tr
        className={row}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`Assinatura de ${group.buyerName}: ${group.productName}, ${chargeCountLabel(group.chargeCount)}`}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onToggle()
          }
        }}
      >
        <td className={td}>
          <div className={buyerCell}>
            <span className={buyerName}>{group.buyerName}</span>
            <span className={productRow}>
              <span className={buyerProduct}>
                {group.productName} · desde {formatMonth(group.firstAt)} ·{' '}
                {chargeCountLabel(group.chargeCount)}
              </span>
              {group.affiliateSale ? <span className={funnelTag}>Afiliado</span> : null}
              {group.charges.some(subscriptionChargeHasCoupon) ? (
                <span className={funnelTag}>Cupom</span>
              ) : null}
              {group.originSaleId ? <span className={funnelTag}>Etapa do funil</span> : null}
            </span>
          </div>
        </td>
        <td className={`${td} ${hideOnMobile}`}>{formatDateTime(group.lastAt)}</td>
        <td className={td}>
          <div className={subBadgeRow}>
            <StatusBadge
              tone={badge.tone}
              label={badge.label}
              size="sm"
              icon={<Repeat size={13} />}
            />
          </div>
          {overdueHint ? <span className={pendingHint}>{overdueHint}</span> : null}
        </td>
        <td className={`${td} ${alignRight}`}>
          {group.hasFinancialImpact ? (
            <SaleImpactCopy
              active={group.accumulated}
              original={group.originalAccumulated}
              affected={group.affectedAccumulated}
              align="end"
            />
          ) : (
            <MoneyValue
              amount={group.accumulated.amount}
              currency={group.accumulated.currency}
              size="sm"
            />
          )}
          <span className={expandToggle}>
            acumulado {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </span>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td className={expandCell} colSpan={4}>
            <SubscriptionCycles
              group={group}
              onSelect={onSelect}
              onViewSubscription={onViewSubscription}
            />
          </td>
        </tr>
      ) : null}
    </>
  )
}

function LoadedCycleRow({
  charge,
  group,
  onSelect,
}: {
  charge: SubscriptionCharge
  group: SubscriptionGroup
  onSelect: (saleId: string, scope?: 'charge') => void
}) {
  const presentation = getChargeStatusPresentation(charge.sale)
  const impact = summarizeSubscriptionCharge(charge)
  const extraTags = subscriptionChargeExtraTags(charge)
  return (
    <div
      className={cycleRow}
      role="button"
      tabIndex={0}
      aria-label={`Ver detalhe da cobrança ${cycleLabel(charge, group)} de ${group.buyerName}`}
      onClick={() => {
        onSelect(charge.sale.id, 'charge')
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect(charge.sale.id, 'charge')
        }
      }}
    >
      <div className={cycleLead}>
        <span className={cycleTitleRow}>
          <span className={cycleTitle}>{cycleLabel(charge, group)}</span>
          {subscriptionChargeHasCoupon(charge) ? <span className={funnelTag}>Cupom</span> : null}
          {extraTags.map((tag) => (
            <span key={tag} className={funnelTag}>
              {tag}
            </span>
          ))}
        </span>
        <SaleIdHint saleId={charge.sale.id} />
        <span className={cycleMeta}>
          {charge.sale.productName} · {formatDateTime(charge.sale.createdAt)}
        </span>
      </div>
      <div className={cycleAside}>
        <StatusBadge tone={presentation.tone} label={presentation.label} size="sm" />
        {impact.hasImpact ? (
          <SaleImpactCopy
            active={impact.active}
            original={impact.original}
            affected={impact.affected}
            align="end"
          />
        ) : (
          <span className={cycleAmount}>
            <MoneyValue
              amount={(charge.sale.participationGrossAmount ?? impact.original).amount}
              currency={(charge.sale.participationGrossAmount ?? impact.original).currency}
              size="sm"
            />
          </span>
        )}
      </div>
    </div>
  )
}

function LazySubscriptionCycles({
  group,
  onSelect,
}: {
  group: SubscriptionGroup
  onSelect: (saleId: string, scope?: 'charge') => void
}) {
  const charges = useSaleChargesQuery(group.charges[0]!.sale.id)
  const loaded = charges.data?.pages.flatMap((page) => page.items)
  const rows: SubscriptionCharge[] =
    loaded?.map((sale) => ({
      sale,
      cycleN: sale.subscriptionCycleN ?? 1,
      isRenewal: sale.isRenewal ?? false,
      funnelChildren: [],
    })) ?? individualSubscriptionCharges(group.charges)
  return (
    <>
      {rows.map((charge) => (
        <LoadedCycleRow key={charge.sale.id} charge={charge} group={group} onSelect={onSelect} />
      ))}
      {charges.isPending ? <Skeleton height={48} /> : null}
      {charges.isError ? (
        <div>
          <p className={cycleMeta}>Não foi possível carregar o restante do histórico.</p>
          <Button variant="secondary" onClick={() => void charges.refetch()}>
            Tentar novamente
          </Button>
        </div>
      ) : charges.hasNextPage ? (
        <Button
          variant="secondary"
          disabled={charges.isFetchingNextPage}
          onClick={() => void charges.fetchNextPage()}
        >
          Carregar mais cobranças
        </Button>
      ) : null}
    </>
  )
}

function SubscriptionCycles({
  group,
  onSelect,
  onViewSubscription,
}: {
  group: SubscriptionGroup
  onSelect: (saleId: string, scope?: 'charge') => void
  onViewSubscription: (subscriptionId: string) => void
}) {
  const loadedCharges = individualSubscriptionCharges(group.charges)
  const needsFetch = group.chargeCount > loadedCharges.length
  return (
    <>
      <div className={cycleList}>
        {needsFetch ? (
          <LazySubscriptionCycles group={group} onSelect={onSelect} />
        ) : (
          loadedCharges.map((charge) => (
            <LoadedCycleRow
              key={charge.sale.id}
              charge={charge}
              group={group}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
      <div className={subFooter}>
        <Button
          variant="secondary"
          leftIcon={<Repeat size={16} />}
          onClick={() => {
            onViewSubscription(group.subscriptionId)
          }}
        >
          Ver assinatura
        </Button>
      </div>
    </>
  )
}

function JourneyMobileCard({
  journey,
  onSelect,
}: {
  journey: SaleJourney
  onSelect: (saleId: string, scope?: 'charge') => void
}) {
  const { sale } = journey
  const summary = summarizeJourney(journey)
  const statuses = summarizeJourneyStatuses(journey)
  const presentation = getJourneyStatusPresentation({ ...sale, funnelStages: journey.children })
  const StatusIcon = presentation.icon
  const hasCoupon = journeyHasCoupon(journey)

  function open() {
    onSelect(sale.id)
  }

  return (
    <div
      className={`${mobileCard} ${mobileCardInteractive}`}
      role="button"
      tabIndex={0}
      aria-label={`Ver detalhe da venda de ${sale.buyerName}: ${sale.productName}`}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          open()
        }
      }}
    >
      <div className={mobileCardHeader}>
        <span className={mobileCardLead}>
          <span className={mobileCardTitle}>{sale.buyerName}</span>
          <span className={mobileCardMeta}>{sale.productName}</span>
          <SaleIdHint saleId={sale.id} />
        </span>
        <span className={mobileCardAside}>
          {!summary.hasFinancialImpact ? (
            <MoneyValue amount={summary.total.amount} currency={summary.total.currency} size="sm" />
          ) : null}
          <StatusBadge
            tone={presentation.tone}
            label={presentation.label}
            size="sm"
            icon={<StatusIcon size={13} />}
          />
        </span>
      </div>
      {summary.hasFinancialImpact ? (
        <SaleImpactCopy
          active={summary.total}
          original={summary.originalTotal}
          affected={summary.affectedTotal}
        />
      ) : null}
      <div className={mobileCardDetails}>
        <span className={mobileCardDetail}>
          <span className={mobileDetailLabel}>Data</span>
          <span className={mobileDetailValue}>{formatDateTime(sale.createdAt)}</span>
        </span>
        <span className={mobileCardDetail}>
          <span className={mobileDetailLabel}>Origem</span>
          <span className={mobileDetailValue}>
            {sale.globalMarket ? 'Mercado Global' : 'Venda direta'}
          </span>
        </span>
      </div>
      {sale.affiliateSale === true ||
      hasCoupon ||
      summary.itemCount > 0 ||
      statuses.chargeCount > 1 ? (
        <span className={productRow}>
          {hasCoupon ? <span className={funnelTag}>Cupom</span> : null}
          {sale.affiliateSale === true ? <span className={funnelTag}>Afiliado</span> : null}
          {summary.embeddedItemCount > 0 ? (
            <span className={funnelTag}>{embeddedItemLabel(summary.embeddedItemCount)}</span>
          ) : null}
          {summary.separateChargeCount > 0 ? (
            <span className={funnelTag}>{separateChargeLabel(summary.separateChargeCount)}</span>
          ) : null}
          {statuses.chargeCount > 1 ? <span className={pendingHint}>{statuses.label}</span> : null}
        </span>
      ) : null}
    </div>
  )
}

function SubscriptionMobileCard({
  group,
  expanded,
  onToggle,
  onSelect,
  onViewSubscription,
}: {
  group: SubscriptionGroup
  expanded: boolean
  onToggle: () => void
  onSelect: (saleId: string, scope?: 'charge') => void
  onViewSubscription: (subscriptionId: string) => void
}) {
  const badge = healthBadge(group)
  const overdueHint =
    group.health === 'overdue'
      ? group.latestStatus === 'FAILED'
        ? 'última cobrança falhou'
        : 'última cobrança em aberto'
      : null

  return (
    <div className={mobileCard}>
      <div
        className={mobileCardButton}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`Assinatura de ${group.buyerName}: ${group.productName}, ${chargeCountLabel(group.chargeCount)}`}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onToggle()
          }
        }}
      >
        <div className={mobileCardHeader}>
          <span className={mobileCardLead}>
            <span className={mobileCardTitle}>{group.buyerName}</span>
            <span className={mobileCardMeta}>
              {group.productName} · desde {formatMonth(group.firstAt)} ·{' '}
              {chargeCountLabel(group.chargeCount)}
            </span>
          </span>
          <span className={mobileCardAside}>
            {!group.hasFinancialImpact ? (
              <MoneyValue
                amount={group.accumulated.amount}
                currency={group.accumulated.currency}
                size="sm"
              />
            ) : null}
            <StatusBadge
              tone={badge.tone}
              label={badge.label}
              size="sm"
              icon={<Repeat size={13} />}
            />
          </span>
        </div>
        {group.hasFinancialImpact ? (
          <SaleImpactCopy
            active={group.accumulated}
            original={group.originalAccumulated}
            affected={group.affectedAccumulated}
          />
        ) : null}
        <div className={mobileCardDetails}>
          <span className={mobileCardDetail}>
            <span className={mobileDetailLabel}>Última cobrança</span>
            <span className={mobileDetailValue}>{formatDateTime(group.lastAt)}</span>
          </span>
          <span className={mobileCardDetail}>
            <span className={mobileDetailLabel}>Histórico</span>
            <span className={mobileDetailValue}>
              acumulado {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </span>
            {overdueHint ? <span className={pendingHint}>{overdueHint}</span> : null}
          </span>
        </div>
        {group.affiliateSale ||
        group.originSaleId ||
        group.charges.some(subscriptionChargeHasCoupon) ? (
          <span className={productRow}>
            {group.affiliateSale ? <span className={funnelTag}>Afiliado</span> : null}
            {group.charges.some(subscriptionChargeHasCoupon) ? (
              <span className={funnelTag}>Cupom</span>
            ) : null}
            {group.originSaleId ? <span className={funnelTag}>Etapa do funil</span> : null}
          </span>
        ) : null}
      </div>
      {expanded ? (
        <SubscriptionCycles
          group={group}
          onSelect={onSelect}
          onViewSubscription={onViewSubscription}
        />
      ) : null}
    </div>
  )
}

export function SalesTable({ rows, onSelect, onViewSubscription }: SalesTableProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const isMobile = useMediaQuery('(max-width: 640px)')

  const toggle = (subscriptionId: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(subscriptionId)) {
        next.delete(subscriptionId)
      } else {
        next.add(subscriptionId)
      }
      return next
    })
  }

  if (isMobile) {
    return (
      <div className={mobileList}>
        {rows.map((entry) =>
          entry.type === 'subscription' ? (
            <SubscriptionMobileCard
              key={`mobile-sub:${entry.group.subscriptionId}`}
              group={entry.group}
              expanded={expanded.has(entry.group.subscriptionId)}
              onToggle={() => toggle(entry.group.subscriptionId)}
              onSelect={onSelect}
              onViewSubscription={onViewSubscription}
            />
          ) : (
            <JourneyMobileCard
              key={`mobile-sale:${entry.journey.sale.id}`}
              journey={entry.journey}
              onSelect={onSelect}
            />
          ),
        )}
      </div>
    )
  }

  return (
    <table className={table}>
      <thead>
        <tr>
          <th className={th}>Comprador</th>
          <th className={`${th} ${hideOnMobile}`}>Data</th>
          <th className={th}>Status</th>
          <th className={`${th} ${alignRight}`}>Valor</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((entry) =>
          entry.type === 'subscription' ? (
            <SubscriptionRow
              key={`sub:${entry.group.subscriptionId}`}
              group={entry.group}
              expanded={expanded.has(entry.group.subscriptionId)}
              onToggle={() => toggle(entry.group.subscriptionId)}
              onSelect={onSelect}
              onViewSubscription={onViewSubscription}
            />
          ) : (
            <JourneyRow key={entry.journey.sale.id} journey={entry.journey} onSelect={onSelect} />
          ),
        )}
      </tbody>
    </table>
  )
}
